import { EditOutlined, PlusCircleOutlined, ReadOutlined, SolutionOutlined } from '@ant-design/icons'
import { usePrevious } from 'ahooks'
import { Button, List, Typography } from 'antd'
import moment from 'moment'
import React from 'react'
import { useTranslation } from 'react-i18next'

import { CareerProfile, CareerProfileFragment, ExperienceType, useGetCareerProfileQuery } from '@/generated/graphql'

import { UserInfoSectionLayout } from '../user-info-section-layout/user-info-section-layout.component'
import { UserInfoProps } from '../user-info.interface'
import s from '../user-info.module.less'
import { UserInfoBackgroundModal } from './user-info-background-modal.component'

export interface BackgroundInputType {
  experience?: CareerProfileFragment | undefined
  type: ExperienceType
}

export const initialInputState = {
  type: ExperienceType.Career,
}

export const UserInfoBackgroundInner: React.FC<
  UserInfoProps & {
    input: BackgroundInputType
    setInput: React.Dispatch<React.SetStateAction<BackgroundInputType>>
    backgroundType: ExperienceType
    bodyStyle?: any
  }
> = ({ userId, input, setInput, readonly, backgroundType, bodyStyle }) => {
  const [visible, setVisible] = React.useState(false)
  const [careers, setCareers] = React.useState<CareerProfile[]>([])
  const previous = usePrevious(input)

  const { data } = useGetCareerProfileQuery({
    variables: {
      userId,
    },
  })

  React.useEffect(() => {
    if (data) {
      const careers = data.user.careerProfile.slice().filter((career) => career.experienceType === backgroundType)
      setCareers(careers)
    }
  }, [data])

  React.useEffect(() => {
    // 非初始化时弹出 modal
    if (previous) {
      setVisible(true)
    }
  }, [input])

  const hasCareers = careers.length > 0

  const careersNode = (
    <List>
      {careers.map((career) => (
        <List.Item
          key={career.pastExperienceId}
          actions={
            !readonly
              ? [
                  <EditOutlined
                    key='edit'
                    style={{ fontSize: 20, marginRight: 15 }}
                    onClick={() => {
                      setInput({ experience: career, type: career.experienceType })
                    }}
                  />,
                ]
              : undefined
          }
        >
          <div className={s.backgroundItem}>
            <h3 className={s.backgroundPosition}>{career.organization}</h3>
            <p className={s.backgroundOrganization}>{career.position}</p>
            <span className={s.backgroundDuration}>
              {career.duration
                .split('_')
                .map((value) => moment(value).format('YYYY'))
                .join(' - ')}
            </span>
            <div className={s.backgroundDescription}>{career.jobDescription}</div>
          </div>
        </List.Item>
      ))}
    </List>
  )

  return (
    <div style={bodyStyle}>
      <Typography className={s.background}>
        {(!readonly || hasCareers) && (
          <Typography.Paragraph className={s.backgroundSection}>{careersNode}</Typography.Paragraph>
        )}
      </Typography>
      <UserInfoBackgroundModal input={input} setInput={setInput} visible={visible} setVisible={setVisible} />
    </div>
  )
}

export const UserInfoBackground: React.FC<UserInfoProps & { backgroundType: ExperienceType }> = ({
  userId,
  readonly,
  backgroundType,
}) => {
  const { t } = useTranslation()
  const [input, setInput] = React.useState<BackgroundInputType>({ type: backgroundType })

  const { data } = useGetCareerProfileQuery({
    variables: {
      userId,
    },
  })

  const hasBackgrounds =
    (data?.user.careerProfile || []).filter((career) => career.experienceType === backgroundType).length > 0

  if (!hasBackgrounds && readonly) {
    return null
  }

  return (
    <UserInfoSectionLayout
      title={t(`user.info.${backgroundType.toLowerCase()}.background`)}
      icon={backgroundType === ExperienceType.Career ? <SolutionOutlined /> : <ReadOutlined />}
      showActions={true}
      actions={
        !readonly && (
          <Button
            size='large'
            type='text'
            className={s.backgroundAddButton}
            icon={<PlusCircleOutlined style={{ color: '#48bbb5' }} />}
            onClick={() => {
              setInput({ experience: undefined, type: backgroundType })
            }}
          >
            {t('user.info.career.placeholder')}
          </Button>
        )
      }
    >
      <UserInfoBackgroundInner
        userId={userId}
        readonly={readonly}
        backgroundType={backgroundType}
        input={input}
        setInput={setInput}
      />
    </UserInfoSectionLayout>
  )
}
